import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ExpandMore,
  Cancel,
  Receipt,
  Warning,
  CheckCircle,
  Pending,
  MoneyOff,
  Money,
  Restore,
  CreditCard,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { useNavigate } from 'react-router-dom';

interface Cancellation {
  _id: string;
  status: 'cancelled' | 'pending_cancellation';
  cancellationBillingStatus?: 'no_charge' | 'upcoming_service_chargeable' | 'refund_required' | 'contract_termination';
  cancellationType: 'standard' | 'seasonal_early_termination' | 'one_time' | 'admin_override';
  cancellationDate: string;
  cancellationReason?: string;
  refundStatus?: 'pending_review' | 'no_refund' | 'partial_refund_approved' | 'full_refund_approved' | 'refund_processed' | 'provider_adjustment_required';
  refundAmount?: number;
  seasonalContractSummary?: any;
  providerPayoutAdjusted?: boolean;
  customerId: any;
  providerId: any;
  serviceType: any;
  address: any;
  cancelledBy: any;
}

const AdminCancellationsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cancellations, setCancellations] = useState<Cancellation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedCancellation, setSelectedCancellation] = useState<Cancellation | null>(null);
  const [seasonalSummaryDialog, setSeasonalSummaryDialog] = useState({ open: false, cancellationId: '' });
  const [refundDialog, setRefundDialog] = useState({ open: false, cancellation: null as Cancellation | null });
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [financialSummary, setFinancialSummary] = useState<any>(null);
  const [processingRefund, setProcessingRefund] = useState(false);
  const [goodwillDialog, setGoodwillDialog] = useState({ open: false });
  const [processingGoodwill, setProcessingGoodwill] = useState(false);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [revivingId, setRevivingId] = useState<string | null>(null);
  const [lateChargeDialog, setLateChargeDialog] = useState<{
    open: boolean;
    cancellation: Cancellation | null;
    suggestedAmountDollars: number;
    canCharge: boolean;
    reason?: string;
  }>({ open: false, cancellation: null, suggestedAmountDollars: 0, canCharge: false });
  const [lateChargeAmount, setLateChargeAmount] = useState('');
  const [lateChargeNotes, setLateChargeNotes] = useState('');
  const [chargingLateId, setChargingLateId] = useState<string | null>(null);

  // Filter cancellations based on selected tab
  const filteredCancellations = cancellations.filter((cancellation) => {
    if (selectedTab === 0) return true; // All
    if (selectedTab === 1) return (cancellation.cancellationType ?? '') === 'seasonal_early_termination';
    if (selectedTab === 2) return (cancellation.status ?? '') === 'pending_cancellation';
    if (selectedTab === 3) return (cancellation.refundStatus ?? '') === 'pending_review';
    return true;
  });

  useEffect(() => {
    fetchCancellations();
  }, []);

  const fetchCancellations = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.get('/admin/cancellations?limit=100');
      
      if (response.success && response.data) {
        // Ensure response.data is an array
        const cancellationsData = Array.isArray(response.data) ? response.data : [];
        setCancellations(cancellationsData);
      } else {
        setError('Failed to load cancellations');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load cancellations');
    } finally {
      setLoading(false);
    }
  };

  const handleViewSeasonalSummary = async (cancellationId: string) => {
    try {
      setLoadingSummary(true);
      setError('');
      const response = await apiService.get(`/admin/cancellations/${cancellationId}/seasonal-summary`);
      
      if (response.success && response.data) {
        setFinancialSummary(response.data);
        setSeasonalSummaryDialog({ open: true, cancellationId });
      } else {
        setError('Failed to load seasonal contract summary');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load seasonal contract summary');
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleProcessRefund = async (cancellation: Cancellation) => {
    setRefundDialog({ open: true, cancellation });
  };

  const handleConfirmCancellation = async (cancellation: Cancellation) => {
    try {
      setConfirmingId(cancellation._id);
      setError('');
      const response = await apiService.post(`/admin/cancellations/${cancellation._id}/confirm-cancellation`);
      if (response.success) {
        setSuccess(response.message || 'Cancellation confirmed.');
        await fetchCancellations();
      } else {
        setError(response.error || 'Failed to confirm cancellation');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to confirm cancellation');
    } finally {
      setConfirmingId(null);
    }
  };

  const handleReviveService = async (cancellation: Cancellation) => {
    try {
      setRevivingId(cancellation._id);
      setError('');
      const response = await apiService.post(`/admin/service-requests/${cancellation._id}/revive`);
      if (response.success) {
        setSuccess(response.message || 'Service revived.');
        await fetchCancellations();
      } else {
        setError(response.error || 'Failed to revive service');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to revive service');
    } finally {
      setRevivingId(null);
    }
  };

  const handleOpenLateChargeDialog = async (cancellation: Cancellation) => {
    try {
      setError('');
      const response = await apiService.get<{
        suggestedAmountDollars: number;
        canCharge: boolean;
        reason?: string;
      }>(`/admin/cancellations/${cancellation._id}/suggested-late-charge`);
      if (response.success && response.data) {
        const { suggestedAmountDollars, canCharge, reason } = response.data;
        setLateChargeDialog({
          open: true,
          cancellation,
          suggestedAmountDollars: suggestedAmountDollars ?? 0,
          canCharge: canCharge ?? false,
          reason,
        });
        setLateChargeAmount(suggestedAmountDollars > 0 ? String(suggestedAmountDollars) : '');
        setLateChargeNotes('');
      } else {
        setError(response.error || 'Could not load suggested amount');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Could not load suggested amount');
    }
  };

  const handleCloseLateChargeDialog = () => {
    setLateChargeDialog({ open: false, cancellation: null, suggestedAmountDollars: 0, canCharge: false });
    setLateChargeAmount('');
    setLateChargeNotes('');
  };

  const handleSubmitLateCharge = async () => {
    if (!lateChargeDialog.cancellation) return;
    const amount = parseFloat(lateChargeAmount);
    if (isNaN(amount) || amount <= 0) {
      setError('Enter a valid amount greater than 0');
      return;
    }
    try {
      setChargingLateId(lateChargeDialog.cancellation._id);
      setError('');
      const response = await apiService.post(`/admin/cancellations/${lateChargeDialog.cancellation._id}/charge-late-cancellation`, {
        amountDollars: amount,
        notes: lateChargeNotes.trim() || undefined,
      });
      if (response.success) {
        setSuccess(response.message || 'Customer charged for late cancellation.');
        handleCloseLateChargeDialog();
        await fetchCancellations();
      } else {
        setError(response.error || 'Failed to charge customer');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to charge customer');
    } finally {
      setChargingLateId(null);
    }
  };

  const handleSubmitRefund = async (formData: any) => {
    if (!refundDialog.cancellation) return;

    try {
      setProcessingRefund(true);
      setError('');
      const response = await apiService.post(`/admin/cancellations/${refundDialog.cancellation._id}/process-refund`, formData);
      
      if (response.success) {
        setSuccess(response.message || 'Refund processed successfully');
        setRefundDialog({ open: false, cancellation: null });
        await fetchCancellations(); // Refresh list
      } else {
        setError(response.error || 'Failed to process refund');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to process refund');
    } finally {
      setProcessingRefund(false);
    }
  };

  const formatCurrency = (cents: number | undefined) => {
    if (!cents) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'cancelled':
        return 'error';
      case 'pending_cancellation':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getRefundStatusColor = (status?: string) => {
    switch (status) {
      case 'pending_review':
        return 'warning';
      case 'no_refund':
        return 'default';
      case 'partial_refund_approved':
      case 'full_refund_approved':
        return 'info';
      case 'refund_processed':
        return 'success';
      case 'provider_adjustment_required':
        return 'error';
      default:
        return 'default';
    }
  };

  const getRefundStatusLabel = (status?: string) => {
    switch (status) {
      case 'pending_review':
        return 'Pending Review';
      case 'no_refund':
        return 'No Refund';
      case 'partial_refund_approved':
        return 'Partial Refund Approved';
      case 'full_refund_approved':
        return 'Full Refund Approved';
      case 'refund_processed':
        return 'Refund Processed';
      case 'provider_adjustment_required':
        return 'Provider Adjustment Required';
      default:
        return 'N/A';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Cancellation Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage service cancellations and seasonal contract terminations
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="info"
            startIcon={<Money />}
            onClick={() => setGoodwillDialog({ open: true })}
          >
            Good Faith Refund/Payment
          </Button>
          <Button
            variant="outlined"
            onClick={fetchCancellations}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)}>
          <Tab label={`All (${cancellations.length})`} />
          <Tab label={`Seasonal Contracts (${cancellations.filter(c => (c.cancellationType ?? '') === 'seasonal_early_termination').length})`} />
          <Tab label={`Pending Cancellation (${cancellations.filter(c => (c.status ?? '') === 'pending_cancellation').length})`} />
          <Tab label={`Pending Review (${cancellations.filter(c => (c.refundStatus ?? '') === 'pending_review').length})`} />
        </Tabs>
      </Paper>

      {/* Cancellations Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Customer</TableCell>
              <TableCell>Service</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Refund Status</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCancellations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No cancellations found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredCancellations.map((cancellation) => (
                <TableRow key={cancellation._id} hover>
                  <TableCell>
                    {cancellation.customerId
                      ? `${cancellation.customerId.firstName || ''} ${cancellation.customerId.lastName || ''}`.trim() || cancellation.customerId.email
                      : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {cancellation.serviceType?.name || cancellation.serviceType?.category || 'Service'}
                  </TableCell>
                  <TableCell>
                    {cancellation.address
                      ? `${cancellation.address.street}, ${cancellation.address.city}, ${cancellation.address.state}`
                      : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={(cancellation.cancellationType ?? 'standard').replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                      size="small"
                      color={(cancellation.cancellationType ?? '') === 'seasonal_early_termination' ? 'warning' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={(cancellation.status ?? 'cancelled').replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                      size="small"
                      color={getStatusColor(cancellation.status ?? 'cancelled') as any}
                    />
                  </TableCell>
                  <TableCell>
                    {cancellation.refundStatus && (
                      <Chip
                        label={getRefundStatusLabel(cancellation.refundStatus)}
                        size="small"
                        color={getRefundStatusColor(cancellation.refundStatus) as any}
                      />
                    )}
                    {cancellation.refundAmount && cancellation.refundAmount > 0 && (
                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                        {formatCurrency(cancellation.refundAmount)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {cancellation.cancellationDate
                      ? new Date(cancellation.cancellationDate).toLocaleDateString()
                      : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {(cancellation.status ?? '') === 'pending_cancellation' && (
                        <>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<CheckCircle />}
                            onClick={() => handleConfirmCancellation(cancellation)}
                            disabled={confirmingId === cancellation._id}
                          >
                            {confirmingId === cancellation._id ? 'Confirming...' : 'Confirm Cancellation'}
                          </Button>
                          {(cancellation.cancellationBillingStatus ?? '') === 'upcoming_service_chargeable' && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="secondary"
                              startIcon={<CreditCard />}
                              onClick={() => handleOpenLateChargeDialog(cancellation)}
                            >
                              Charge for late cancellation
                            </Button>
                          )}
                        </>
                      )}
                      {(cancellation.cancellationType ?? '') === 'seasonal_early_termination' && (
                        <>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Receipt />}
                            onClick={() => handleViewSeasonalSummary(cancellation._id)}
                          >
                            Summary
                          </Button>
                          {(cancellation.refundStatus ?? '') === 'pending_review' && (
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              startIcon={<Money />}
                              onClick={() => handleProcessRefund(cancellation)}
                            >
                              Process Refund
                            </Button>
                          )}
                        </>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Restore />}
                        onClick={() => handleReviveService(cancellation)}
                        disabled={revivingId === cancellation._id}
                      >
                        {revivingId === cancellation._id ? 'Reviving...' : 'Revive'}
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Seasonal Contract Financial Summary Dialog */}
      <Dialog
        open={seasonalSummaryDialog.open}
        onClose={() => setSeasonalSummaryDialog({ open: false, cancellationId: '' })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Seasonal Contract Financial Summary</DialogTitle>
        <DialogContent>
          {loadingSummary ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : financialSummary ? (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Contract Overview
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Total Contract Value
                          </Typography>
                          <Typography variant="h6">
                            {formatCurrency(financialSummary.financialSummary?.totalContractValue)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Total Paid
                          </Typography>
                          <Typography variant="h6">
                            {formatCurrency(financialSummary.financialSummary?.totalPaid)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Payment Schedule
                          </Typography>
                          <Typography variant="body1">
                            {financialSummary.financialSummary?.paymentSchedule === 'single-payment'
                              ? 'One-Time Payment'
                              : 'Monthly Payments'}
                          </Typography>
                        </Grid>
                        {financialSummary.financialSummary?.monthlyPaymentAmount && (
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Monthly Payment Amount
                            </Typography>
                            <Typography variant="body1">
                              {formatCurrency(financialSummary.financialSummary.monthlyPaymentAmount)}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Services
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <Typography variant="body2" color="text.secondary">
                            Total Scheduled
                          </Typography>
                          <Typography variant="h6">
                            {financialSummary.financialSummary?.totalServicesScheduled || 0}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2" color="text.secondary">
                            Completed
                          </Typography>
                          <Typography variant="h6" color="success.main">
                            {financialSummary.financialSummary?.servicesCompleted || 0}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2" color="text.secondary">
                            Remaining
                          </Typography>
                          <Typography variant="h6" color="warning.main">
                            {financialSummary.financialSummary?.servicesRemaining || 0}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Financial Breakdown
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Completed Services Value
                          </Typography>
                          <Typography variant="h6">
                            {formatCurrency(financialSummary.financialSummary?.completedServicesValue)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Remaining Unused Value
                          </Typography>
                          <Typography variant="h6" color="warning.main">
                            {formatCurrency(financialSummary.financialSummary?.remainingUnusedValue)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                {financialSummary.providerPayoutInfo && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Provider Payout Information
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Total Payouts Received
                            </Typography>
                            <Typography variant="h6">
                              {formatCurrency(financialSummary.providerPayoutInfo?.totalPayoutsReceived)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Adjustment Required
                            </Typography>
                            <Chip
                              label={financialSummary.providerPayoutInfo?.needsAdjustment ? 'Yes' : 'No'}
                              color={financialSummary.providerPayoutInfo?.needsAdjustment ? 'warning' : 'success'}
                              size="small"
                            />
                          </Grid>
                          {financialSummary.providerPayoutInfo?.adjustmentAmount && (
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary">
                                Adjustment Amount
                              </Typography>
                              <Typography variant="h6" color="error.main">
                                {formatCurrency(financialSummary.providerPayoutInfo.adjustmentAmount)}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSeasonalSummaryDialog({ open: false, cancellationId: '' })}>
            Close
          </Button>
          {financialSummary && financialSummary.serviceRequest?.refundStatus === 'pending_review' && (
            <Button
              variant="contained"
              onClick={() => {
                setSeasonalSummaryDialog({ open: false, cancellationId: '' });
                const cancellation = cancellations.find(c => c._id === seasonalSummaryDialog.cancellationId);
                if (cancellation) {
                  handleProcessRefund(cancellation);
                }
              }}
            >
              Process Refund
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Process Refund Dialog */}
      <RefundProcessingDialog
        open={refundDialog.open}
        cancellation={refundDialog.cancellation}
        financialSummary={financialSummary}
        onClose={() => setRefundDialog({ open: false, cancellation: null })}
        onSubmit={handleSubmitRefund}
        processing={processingRefund}
      />

      <GoodwillPaymentDialog
        open={goodwillDialog.open}
        onClose={() => setGoodwillDialog({ open: false })}
        onSuccess={() => {
          setGoodwillDialog({ open: false });
          setSuccess('Good faith payment processed.');
        }}
      />

      {/* Charge for late cancellation dialog */}
      <Dialog open={lateChargeDialog.open} onClose={handleCloseLateChargeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Charge for late cancellation</DialogTitle>
        <DialogContent>
          {lateChargeDialog.cancellation && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Customer cancelled after the cutoff window. Charge their saved payment method the service price (or an agreed amount).
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Customer: {lateChargeDialog.cancellation.customerId
                  ? `${lateChargeDialog.cancellation.customerId.firstName || ''} ${lateChargeDialog.cancellation.customerId.lastName || ''}`.trim() || lateChargeDialog.cancellation.customerId.email
                  : 'N/A'}
              </Typography>
              <Typography variant="body2">
                Service: {lateChargeDialog.cancellation.serviceType?.name || lateChargeDialog.cancellation.serviceType?.category || 'Service'}
              </Typography>
              {lateChargeDialog.suggestedAmountDollars > 0 && (
                <Typography variant="body2" color="primary.main" sx={{ mt: 1 }}>
                  Suggested amount: ${lateChargeDialog.suggestedAmountDollars.toFixed(2)}
                </Typography>
              )}
              {lateChargeDialog.reason && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {lateChargeDialog.reason}
                </Alert>
              )}
            </Box>
          )}
          <TextField
            fullWidth
            label="Amount (USD)"
            type="number"
            inputProps={{ min: 0.01, step: 0.01 }}
            value={lateChargeAmount}
            onChange={(e) => setLateChargeAmount(e.target.value)}
            margin="normal"
            helperText="Amount to charge the customer's payment method"
          />
          <TextField
            fullWidth
            label="Notes (optional)"
            multiline
            rows={2}
            value={lateChargeNotes}
            onChange={(e) => setLateChargeNotes(e.target.value)}
            margin="normal"
            placeholder="e.g. Late cancellation - day before service"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLateChargeDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmitLateCharge}
            disabled={
              chargingLateId === lateChargeDialog.cancellation?._id ||
              !lateChargeAmount ||
              parseFloat(lateChargeAmount) <= 0
            }
          >
            {chargingLateId === lateChargeDialog.cancellation?._id ? 'Charging...' : 'Charge customer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

// Refund Processing Dialog Component
interface RefundProcessingDialogProps {
  open: boolean;
  cancellation: Cancellation | null;
  financialSummary: any;
  onClose: () => void;
  onSubmit: (formData: any) => void;
  processing: boolean;
}

const RefundProcessingDialog: React.FC<RefundProcessingDialogProps> = ({
  open,
  cancellation,
  financialSummary,
  onClose,
  onSubmit,
  processing,
}) => {
  const [refundDecision, setRefundDecision] = useState<'no_refund' | 'partial_refund' | 'full_refund'>('no_refund');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundNotes, setRefundNotes] = useState('');
  const [providerAdjustmentAmount, setProviderAdjustmentAmount] = useState('');
  const [providerAdjustmentNotes, setProviderAdjustmentNotes] = useState('');
  const [processStripeRefund, setProcessStripeRefund] = useState(false);

  const handleSubmit = () => {
    const formData = {
      refundDecision,
      refundAmount: refundAmount ? parseInt(refundAmount) * 100 : undefined, // Convert dollars to cents
      refundNotes,
      providerAdjustmentAmount: providerAdjustmentAmount ? parseInt(providerAdjustmentAmount) * 100 : undefined,
      providerAdjustmentNotes,
      processStripeRefund,
    };
    onSubmit(formData);
  };

  const maxRefundAmount = financialSummary?.financialSummary?.remainingUnusedValue
    ? financialSummary.financialSummary.remainingUnusedValue / 100
    : 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Process Refund - Seasonal Contract Termination</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Refund Decision</InputLabel>
            <Select
              value={refundDecision}
              onChange={(e) => setRefundDecision(e.target.value as any)}
              label="Refund Decision"
            >
              <MenuItem value="no_refund">No Refund</MenuItem>
              <MenuItem value="partial_refund">Partial Refund</MenuItem>
              <MenuItem value="full_refund">Full Refund</MenuItem>
            </Select>
          </FormControl>

          {refundDecision === 'partial_refund' && (
            <TextField
              fullWidth
              label="Refund Amount (USD)"
              type="number"
              value={refundAmount}
              onChange={(e) => setRefundAmount(e.target.value)}
              sx={{ mb: 2 }}
              helperText={`Maximum: ${maxRefundAmount.toFixed(2)}`}
              inputProps={{ min: 0, max: maxRefundAmount, step: 0.01 }}
            />
          )}

          {refundDecision === 'full_refund' && financialSummary?.financialSummary && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Full refund amount: {maxRefundAmount.toFixed(2)}
            </Alert>
          )}

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Internal Notes"
            value={refundNotes}
            onChange={(e) => setRefundNotes(e.target.value)}
            sx={{ mb: 2 }}
            helperText="Internal notes about refund decision"
          />

          {financialSummary?.providerPayoutInfo?.needsAdjustment && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Provider Payout Adjustment
              </Typography>
              <TextField
                fullWidth
                label="Adjustment Amount (USD)"
                type="number"
                value={providerAdjustmentAmount}
                onChange={(e) => setProviderAdjustmentAmount(e.target.value)}
                sx={{ mb: 2 }}
                helperText="Positive = clawback, Negative = additional payment"
              />
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Adjustment Notes"
                value={providerAdjustmentNotes}
                onChange={(e) => setProviderAdjustmentNotes(e.target.value)}
                sx={{ mb: 2 }}
              />
            </>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Process Stripe Refund</InputLabel>
            <Select
              value={processStripeRefund ? 'yes' : 'no'}
              onChange={(e) => setProcessStripeRefund(e.target.value === 'yes')}
              label="Process Stripe Refund"
            >
              <MenuItem value="no">No - Track Only</MenuItem>
              <MenuItem value="yes">Yes - Process Stripe Refund</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={processing}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={processing || (refundDecision === 'partial_refund' && !refundAmount)}
        >
          {processing ? 'Processing...' : 'Process Refund'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Good Faith Refund/Payment Dialog Component
interface GoodwillPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const GoodwillPaymentDialog: React.FC<GoodwillPaymentDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [recipientType, setRecipientType] = useState<'customer' | 'provider'>('customer');
  const [recipientId, setRecipientId] = useState('');
  const [recipientSearch, setRecipientSearch] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [processStripePayment, setProcessStripePayment] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async () => {
    if (!recipientSearch.trim()) {
      setError('Please enter an email or name to search');
      return;
    }

    try {
      setSearching(true);
      setError('');
      const response = await apiService.get(`/admin/users/search?q=${encodeURIComponent(recipientSearch)}&userType=${recipientType}`);
      
      if (response.success && response.data) {
        // Ensure response.data is an array
        const searchData = Array.isArray(response.data) ? response.data : [];
        setSearchResults(searchData);
        if (searchData.length === 0) {
          setError('No users found');
        }
      } else {
        setError('Failed to search users');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to search users');
    } finally {
      setSearching(false);
    }
  };

  const handleSubmit = async () => {
    if (!recipientId) {
      setError('Please select a recipient');
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    if (!reason.trim()) {
      setError('Please enter a reason (required for audit purposes)');
      return;
    }

    try {
      setProcessing(true);
      setError('');
      const response = await apiService.post('/admin/goodwill-payment', {
        recipientType,
        recipientId,
        amount: parseFloat(amount),
        reason: reason.trim(),
        notes: notes.trim(),
        processStripePayment,
      });

      if (response.success) {
        onSuccess();
        // Reset form
        setRecipientId('');
        setRecipientSearch('');
        setSearchResults([]);
        setAmount('');
        setReason('');
        setNotes('');
      } else {
        setError(response.error || 'Failed to process good faith payment');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to process good faith payment');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Good Faith Refund/Payment</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            This allows you to process a good faith refund to a customer (requires original payment) or payment to a provider directly through Stripe. All transactions are tracked for audit and tax purposes.
          </Alert>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Recipient Type</InputLabel>
            <Select
              value={recipientType}
              onChange={(e) => {
                setRecipientType(e.target.value as any);
                setRecipientId('');
                setSearchResults([]);
                setRecipientSearch('');
              }}
              label="Recipient Type"
            >
              <MenuItem value="customer">Customer (Refund)</MenuItem>
              <MenuItem value="provider">Provider (Payment)</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Search Recipient (Email or Name)"
              value={recipientSearch}
              onChange={(e) => setRecipientSearch(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              sx={{ mb: 1 }}
            />
            <Button
              variant="outlined"
              onClick={handleSearch}
              disabled={searching || !recipientSearch.trim()}
              size="small"
            >
              {searching ? 'Searching...' : 'Search'}
            </Button>
          </Box>

          {searchResults.length > 0 && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Recipient</InputLabel>
              <Select
                value={recipientId}
                onChange={(e) => setRecipientId(e.target.value)}
                label="Select Recipient"
              >
                {searchResults.map((user) => (
                  <MenuItem key={user._id} value={user._id}>
                    {user.firstName} {user.lastName} ({user.email})
                    {user.businessName && ` - ${user.businessName}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            fullWidth
            label="Amount (USD)"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            sx={{ mb: 2 }}
            inputProps={{ min: 0.01, step: 0.01 }}
            helperText="Enter amount in dollars"
          />

          <TextField
            fullWidth
            required
            label="Reason (Required)"
            multiline
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            sx={{ mb: 2 }}
            helperText="Required for audit purposes"
          />

          <TextField
            fullWidth
            label="Internal Notes (Optional)"
            multiline
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Process Through Stripe</InputLabel>
            <Select
              value={processStripePayment ? 'yes' : 'no'}
              onChange={(e) => setProcessStripePayment(e.target.value === 'yes')}
              label="Process Through Stripe"
            >
              <MenuItem value="yes">Yes - Process Payment</MenuItem>
              <MenuItem value="no">No - Track Only</MenuItem>
            </Select>
            <FormHelperText>
              {recipientType === 'customer' 
                ? 'Note: Customer refunds require an original payment. Customer must have at least one completed payment.'
                : 'Note: Provider must have Stripe Connect account set up.'}
            </FormHelperText>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={processing}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={processing || !recipientId || !amount || !reason.trim()}
        >
          {processing ? 'Processing...' : 'Process Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdminCancellationsPage;
